import type { DaresResponse, FriendDTO, FriendsResponse, MonsterDTO, PlayerDTO } from '@shared/protocol/messages';
import type { App } from '../../../app/App';
import { RULES, skillDef } from '../../combat/rules';
import { validateMonster } from '../../combat/budget';
import { HERO_CLASSES, ITEMS, PRESET_MONSTERS, QUESTS, heroClass, itemDef, type ItemSlot } from '../../data/presets';
import { createHero, equip, heroArtId, heroDrawing, heroStats, unequip, unlockedSkills } from '../../systems/hero';
import { PRESET_MONSTER_LIST, monsterArt, monsterFromDTO } from '../../systems/monsters';
import { buyCosmetic, buyItem, chooseStamp, claimQuest, questProgress, addMemory, upgradeItem, nextStoryStage } from '../../systems/progression';
import { MAX_ITEM_LEVEL, itemLevel, upgradeBonus, upgradeCost } from '../../systems/upgrade';
import { COSMETICS, type CosmeticDef } from '../../data/cosmetics';
import { isKid } from '../../systems/policy';
import { changeAge } from '../../../ui/family';
import { accountSection } from '../../../ui/account';
import { stampFace } from '../../../ui/cosmetics';
import { buyPremium, premiumShop } from '../../../ui/premiumShop';
import { readyHeroes } from '../../../ui/readyHeroes';
import { INJURY_LOSSES, injuredFor, restMinutes } from '../../systems/injury';
import { buyHeroSlot, canCreateHero, heroNameProblem, heroSlotCount, heroSlotSku, nextHeroSlot, nextHeroSlotPrice, randomHeroName } from '../../systems/heroSlots';
import { SHOP_PRODUCTS } from '@shared/shop';
import { newSave, type Monster } from '../../systems/model';
import { sanitizeSave } from '../../systems/save';
import { artImg, button, confirmBox, formatDate, meter, modal, promptBox, statChip, stickyNote, thumb } from '../../../ui/components';
import { h, setChildren } from '../../../ui/dom';
import { icon } from '../../../ui/icons';
import { doodleSvg } from '../../../ui/doodleSvg';
import { renderHeroPanel } from './heroPanel';
import { openRankings, rankingsPanel } from '../../../ui/rankings';
import { contestTab } from './contest';
import { daresSection, fanMailSection, sendDareDialog } from './community';
import { refreshDares, unansweredDares } from '../../../networking/community';
import { publishNotebookCard } from '../../../networking/classroom';
import { classPeek, classTab, openFriendNotebook } from './classroom';
import { dailySection } from './daily';
import { fortuneSection } from './fortune';
import { chapterPages, diceBoardSection, startFight, starRow, towerSection } from './overview';
import { shareMonster } from '../../../networking/share';
import { ensureOnline } from '../../../networking/session';
import { LANGUAGES, language, t, type LanguageSetting } from '../../../i18n';
import { canShare, shareMonsterCard } from '../../../ui/share';
import { openFeedback } from '../../../ui/feedback';

/** newhero, class, wiki and memories have no bookmark of their own (they open from Heroes, PVP and Collection). */
export type TabId = 'hero' | 'newhero' | 'monsters' | 'wiki' | 'equipment' | 'quests' | 'memories' | 'friends' | 'class' | 'contest' | 'settings';

export interface NotebookCtx {
  app: App;
  goTab: (t: TabId) => void;
  refresh: () => void;
  /** Number shown on the Friends tab (pending incoming requests). */
  setFriendRequests: (n: number) => void;
  /** Run fn when this page is re-rendered or the notebook closes (e.g. remove a listener). */
  onCleanup: (fn: () => void) => void;
}

const heading = (text: string, sub?: string): HTMLElement =>
  h('header', { class: 'page-header' }, h('h2', { class: 'page-heading' }, text), sub ? h('p', { class: 'page-sub' }, sub) : null);

/** Monsters, Wiki and Diary share the one "Collection" bookmark; this strip switches between them. */
export const COLLECTION_PAGES: { id: TabId; label: string }[] = [
  { id: 'monsters', label: 'My Monsters' },
  { id: 'memories', label: 'Diary' },
];

function collectionStrip(ctx: NotebookCtx, current: TabId): HTMLElement {
  return h(
    'nav',
    { class: 'sub-tabs', 'aria-label': t('Collection') },
    COLLECTION_PAGES.map((p) =>
      h(
        'button',
        { class: `sub-tab ${p.id === current ? 'active' : ''}`, type: 'button', 'aria-current': p.id === current ? 'page' : undefined, on: { click: () => ctx.goTab(p.id) } },
        t(p.label),
      ),
    ),
  );
}

/* ------------------------------ Hero roster ------------------------------ */

function heroTab(ctx: NotebookCtx): HTMLElement {
  const { app } = ctx;
  const { heroes, activeHeroId } = app.store.state;
  const hero = app.store.hero;
  const skills = hero ? unlockedSkills(hero) : [];
  return h(
    'div',
    { class: 'tab-hero' },
    h('div', { class: 'compact-only' }, renderHeroPanel(ctx)),
    heading(t('My Heroes'), t('Pick who goes on the adventure.')),
    hero
      ? h(
          'label',
          { class: 'field' },
          h('span', null, t('Equipped skill')),
          h(
            'select',
            {
              class: 'hand-select',
              on: {
                change: (e: Event) => {
                  const v = (e.target as HTMLSelectElement).value;
                  app.store.update(['hero'], (s) => {
                    const hh = s.heroes.find((x) => x.id === hero.id);
                    if (hh && unlockedSkills(hh).includes(v as never)) hh.skill = v as never;
                  });
                },
              },
            },
            skills.map((id) => h('option', { value: id, selected: id === hero.skill }, `${t(skillDef(id).name)} — ${t(skillDef(id).description)}`)),
          ),
        )
      : null,
    h(
      'div',
      { class: 'card-grid' },
      heroes.map((hh) => {
        const st = heroStats(hh, app.store.state.itemLevels);
        return h(
          'article',
          { class: `doodle-card ${hh.id === activeHeroId ? 'selected' : ''}` },
          thumb(app, heroDrawing(hh), heroArtId(hh), 84),
          h('h3', null, hh.name),
          h('p', { class: 'card-sub' }, `Lv ${hh.level} ${t(heroClass(hh.classId).name)}`),
          h('div', { class: 'chips' }, statChip('HP', st.maxHp), statChip('ATK', st.atk), statChip('DEF', st.def), statChip('LUCK', st.luck)),
          injuredFor(hh) > 0 ? h('p', { class: 'injury-chip' }, '🤕 ', t('Hurt: resting {n} more min', { n: restMinutes(hh) })) : (hh.lossStreak ?? 0) > 0 ? h('p', { class: 'injury-streak' }, t('Losses in a row: {n}/{max}', { n: hh.lossStreak ?? 0, max: INJURY_LOSSES })) : null,
          h(
            'div',
            { class: 'card-actions' },
            hh.id === activeHeroId
              ? h('span', { class: 'stamp' }, t('PLAYING'))
              : button(t('Play as'), { small: true, color: 'yellow', sfx: app, onClick: () => app.store.update(['hero', 'inventory'], (s) => (s.activeHeroId = hh.id)) }),
            button(t('Redraw'), { small: true, sfx: app, onClick: () => void app.scenes.go('creator', { heroMode: true, monsterId: hh.id }) }),
            button(t('Rename'), {
              small: true,
              variant: 'ghost',
              sfx: app,
              onClick: async () => {
                const name = await promptBox(t('Rename hero'), t('New name'), hh.name);
                if (name) app.store.update(['hero'], (s) => ((s.heroes.find((x) => x.id === hh.id) ?? hh).name = name.slice(0, 24)));
              },
            }),
            button(t('Delete'), {
              small: true,
              variant: 'danger',
              sfx: app,
              onClick: async () => {
                if (!(await confirmBox(t('Erase hero?'), t('Rub {name} out of the notebook forever?', { name: hh.name }), t('Erase')))) return;
                app.sfx.play('erase');
                app.store.update(['hero', 'inventory'], (s) => {
                  s.heroes = s.heroes.filter((x) => x.id !== hh.id);
                  if (s.activeHeroId === hh.id) s.activeHeroId = s.heroes[0]?.id ?? null;
                });
                if (!app.store.hero) ctx.goTab('newhero');
              },
            }),
          ),
        );
      }),
      h('button', { class: 'doodle-card add-card', type: 'button', on: { click: () => ctx.goTab('newhero') } }, h('span', { class: 'plus' }, '+'), h('span', null, t('New Hero'))),
    ),
    readyHeroes(ctx),
  );
}

/* ------------------------------ Hero creation ----------------------------- */

/** All hero slots are used: unlock one with coins or for 19 baht. */
function heroSlotsFull(ctx: NotebookCtx): HTMLElement {
  const { app } = ctx;
  const save = app.store.state;
  const slot = nextHeroSlot(save);
  const price = nextHeroSlotPrice(save);
  const product = slot ? SHOP_PRODUCTS.find((p) => p.sku === heroSlotSku(slot)) : undefined;
  return h(
    'div',
    { class: 'tab-newhero hero-slots-full' },
    heading(t('Create a Hero'), t('Your hero slots are full ({n}).', { n: heroSlotCount(save) })),
    slot && price !== null
      ? h(
          'div',
          { class: 'sticky-note yellow hero-slot-note' },
          h('p', null, h('b', null, t('Unlock hero slot {n}', { n: slot }))),
          h('p', null, t('Save up coins from battles and quests, or unlock it right away.')),
          h(
            'div',
            { class: 'row-actions' },
            button(h('span', null, `${t('Unlock')} `, h('span', { class: 'coin', 'aria-hidden': 'true' }), ` ${price}`), {
              color: 'yellow',
              variant: 'primary',
              disabled: save.coins < price,
              title: save.coins < price ? t('Not enough coins yet ({have} / {need})', { have: save.coins, need: price }) : undefined,
              sfx: app,
              onClick: () => {
                let ok = false;
                app.store.update(['inventory', 'hero'], (s) => (ok = buyHeroSlot(s)));
                if (ok) {
                  app.sfx.play('pop');
                  app.toast(t('Hero slot {n} unlocked!', { n: slot }), 'success');
                  ctx.refresh();
                }
              },
            }),
            product ? button(`฿${product.priceTHB}`, { color: 'blue', sfx: app, title: t('Unlock right away with real money'), onClick: () => void buyPremium(app, product) }) : null,
          ),
        )
      : h('p', { class: 'hand-hint' }, t('You have every hero slot. Erase a hero to make room for a new one.')),
  );
}

function newHeroTab(ctx: NotebookCtx): HTMLElement {
  const { app } = ctx;
  if (!canCreateHero(app.store.state)) return heroSlotsFull(ctx);
  let chosen = HERO_CLASSES[0]!.id;
  const nameInput = h('input', { class: 'hand-input big', maxLength: 24, placeholder: t('Your hero\'s name'), value: '', 'aria-label': t('Hero name'), required: true });
  const cards = HERO_CLASSES.map((c) => {
    const card = h(
      'button',
      { class: `doodle-card class-card ${c.id === chosen ? 'selected' : ''}`, type: 'button', 'aria-pressed': String(c.id === chosen) },
      thumb(app, null, `hero:${c.id}`, 92),
      h('h3', null, t(c.name)),
      h('p', { class: 'card-sub' }, t(c.blurb)),
      h('div', { class: 'chips' }, statChip('HP', c.base.hp), statChip('ATK', c.base.atk), statChip('DEF', c.base.def), statChip('LUCK', c.base.luck)),
      h('p', { class: 'skill-line' }, `★ ${t(skillDef(c.skill).name)}`),
    );
    card.addEventListener('click', () => {
      chosen = c.id;
      app.sfx.play('pop');
      for (const el of cards) el.classList.remove('selected');
      card.classList.add('selected');
    });
    return card;
  });
  const name = (): string => nameInput.value.trim();
  const nameError = h('p', { class: 'field-error', role: 'alert' });
  /** A name of their own is required; the class name does not count. */
  const nameOk = (): boolean => {
    const problem = heroNameProblem(nameInput.value, chosen);
    nameError.textContent = problem ?? '';
    if (problem) {
      nameInput.focus();
      app.sfx.play('erase');
    }
    return !problem;
  };
  nameInput.addEventListener('input', () => (nameError.textContent = ''));
  const dice = button('🎲', {
    small: true,
    title: t('A random name'),
    sfx: app,
    onClick: () => {
      nameInput.value = randomHeroName(language());
      nameError.textContent = '';
    },
  });
  return h(
    'div',
    { class: 'tab-newhero' },
    heading(t('Create a Hero'), t('Write a name, pick a class, then use the doodle or draw your own.')),
    h('label', { class: 'field hero-name-field' }, h('span', null, t('Name (required)')), h('span', { class: 'name-row' }, nameInput, dice)),
    nameError,
    h('div', { class: 'card-grid classes' }, cards),
    h(
      'div',
      { class: 'row-actions' },
      button(t('Use this doodle'), {
        variant: 'primary',
        color: 'yellow',
        sfx: app,
        onClick: () => {
          if (!nameOk()) return;
          const hero = createHero(name(), chosen, null);
          app.store.update(['hero', 'memories', 'inventory'], (s) => {
            s.heroes.push(hero);
            s.activeHeroId = hero.id;
            addMemory(s, { kind: 'create', title: t('{name} joined the notebook', { name: hero.name }), text: t('A brand-new {cls}.', { cls: t(heroClass(chosen).name) }), sticker: 'pencil' });
          });
          app.toast(t('{name} is ready for adventure!', { name: hero.name }), 'success');
          ctx.goTab('hero');
        },
      }),
      button(t('✏ Draw my own hero'), {
        color: 'blue',
        sfx: app,
        onClick: () => {
          if (!nameOk()) return;
          sessionStorage.setItem('pl:newhero', JSON.stringify({ name: name(), classId: chosen }));
          void app.scenes.go('creator', { heroMode: true });
        },
      }),
    ),
  );
}

/* ------------------------------ Monsters --------------------------------- */

/** "Sketch · by Mia" — tier name plus author for home-made monsters. */
function tierLine(m: Monster): string {
  const tier = t(RULES.budget.tiers.find((x) => x.id === m.tier)?.name ?? m.tier);
  return m.author ? t('{tier} · by {name}', { tier, name: m.author }) : tier;
}

function monsterCard(ctx: NotebookCtx, m: Monster, kind: 'preset' | 'mine' | 'shared' | 'hall'): HTMLElement {
  const { app } = ctx;
  const art = monsterArt(m) ?? 'monster:gum_blob';
  const defeated = kind === 'preset' && m.preset !== undefined && app.store.state.defeatedPresets.includes(m.preset);
  const preset = kind === 'preset' ? PRESET_MONSTERS.find((p) => p.id === m.preset) : undefined;
  const fight = (): void => {
    if (!app.store.hero) {
      app.toast(t('Draw a hero first — monsters need someone to fight!'), 'info');
      ctx.goTab('newhero');
      return;
    }
    void app.scenes.go('battle', { mode: 'pve', monsterId: m.id }, 'page');
  };
  const actions: HTMLElement[] = [button(t('Fight!'), { small: true, variant: 'primary', color: 'pink', sfx: app, onClick: fight })];
  if (kind === 'mine') {
    actions.push(button(t('Edit'), { small: true, sfx: app, onClick: () => void app.scenes.go('creator', { monsterId: m.id }) }));
    actions.push(button(t('Share'), { small: true, color: 'blue', sfx: app, onClick: () => void shareMonster(app, m) }));
    if (canShare(app)) actions.push(button(t('Facebook'), { small: true, color: 'blue', sfx: app, onClick: () => void shareMonsterCard(app, m) }));
  }
  if (kind === 'mine' || kind === 'shared') {
    actions.push(
      button(t('Delete'), {
        small: true,
        variant: 'danger',
        sfx: app,
        onClick: async () => {
          if (!(await confirmBox(t('Erase monster?'), t('Rub out {name}?', { name: m.name }), t('Erase')))) return;
          app.sfx.play('erase');
          app.store.update(['monsters'], (s) => (s.monsters = s.monsters.filter((x) => x.id !== m.id)));
        },
      }),
    );
  }
  return h(
    'article',
    { class: `doodle-card monster-card tier-${m.tier} ${defeated ? 'defeated' : ''}` },
    defeated ? h('span', { class: 'stamp red' }, t('DEFEATED')) : null,
    defeated && m.preset ? starRow(app.store.state.monsterStars[m.preset] ?? 1) : null,
    m.mode === 'classroom' ? h('span', { class: 'stamp blue' }, t('CLASSROOM')) : null,
    kind === 'hall' ? h('span', { class: 'stamp gold' }, t('CHAMPION')) : null,
    thumb(app, m.drawing, art, 84),
    h('h3', null, kind === 'preset' ? t(m.name) : m.name),
    h('p', { class: 'card-sub' }, preset ? `Lv ${preset.level} · ${t(preset.blurb)}` : tierLine(m)),
    h('div', { class: 'chips' }, statChip('HP', m.hp), statChip('ATK', m.atk), statChip('DEF', m.def), m.luck ? statChip('LUCK', m.luck) : null),
    h('p', { class: 'skill-line' }, `★ ${t(skillDef(m.skill).name)} · ${t(RULES.personalities.find((p) => p.id === m.personality)?.name ?? '')}`),
    h('p', { class: 'reward-line' }, m.mode === 'classroom' ? t('Practice only (no rewards)') : t('Reward: {exp} EXP · {coins} coins', { exp: m.reward.exp, coins: m.reward.coins })),
    h('div', { class: 'card-actions' }, actions),
  );
}

async function importByCode(ctx: NotebookCtx, code: string): Promise<void> {
  const { app } = ctx;
  try {
    const res = await app.api.getSharedMonster(code);
    const check = validateMonster(res.monster);
    if (!check.ok) throw new Error(check.errors.join(', '));
    const m = monsterFromDTO(res.monster, 'shared', res.authorName, res.code);
    app.store.update(['monsters', 'memories', 'friends'], (s) => {
      s.monsters.push(m);
      addMemory(s, { kind: 'friend', title: t('Got {monster} from {name}', { monster: m.name, name: res.authorName }), text: t('Share code {code}.', { code: res.code }), sticker: 'heart' });
    });
    app.toast(t('{name} was taped into your notebook!', { name: m.name }), 'success');
  } catch (err) {
    app.toast(err instanceof Error ? err.message : t('Could not import that monster.'), 'error');
  }
}

function importFile(ctx: NotebookCtx, file: File): void {
  const { app } = ctx;
  file
    .text()
    .then((text) => {
      const dto = JSON.parse(text) as MonsterDTO;
      const check = validateMonster(dto);
      if (!check.ok) throw new Error(check.errors.join(', '));
      const m = monsterFromDTO(dto, 'shared', t('a friend'));
      app.store.update(['monsters'], (s) => s.monsters.push(m));
      app.toast(t('{name} imported!', { name: m.name }), 'success');
    })
    .catch((err: unknown) => app.toast(t('That file is not a Paper Legends monster ({reason}).', { reason: err instanceof Error ? err.message : t('unreadable') }), 'error'));
}

function monstersTab(ctx: NotebookCtx): HTMLElement {
  const { app } = ctx;
  const mine = app.store.state.monsters.filter((m) => m.source === 'mine');
  const shared = app.store.state.monsters.filter((m) => m.source === 'shared');
  const hall = app.store.state.monsters.filter((m) => m.source === 'hall').sort((a, b) => b.createdAt - a.createdAt);
  const codeInput = h('input', { class: 'hand-input code', maxLength: 6, placeholder: 'ABC123', 'aria-label': t('Share code') });
  const fileInput = h('input', { type: 'file', accept: '.json,application/json', class: 'visually-hidden', 'aria-label': t('Import monster file') });
  fileInput.addEventListener('change', () => {
    const f = fileInput.files?.[0];
    if (f) importFile(ctx, f);
  });
  return h(
    'div',
    { class: 'tab-monsters' },
    collectionStrip(ctx, 'monsters'),
    heading(t('Monster Collection'), t('Your doodles, contest winners and monsters from friends.')),
    h('h3', { class: 'section-title' }, t('Hall of Fame')),
    hall.length
      ? h('div', { class: 'card-grid' }, hall.map((m) => monsterCard(ctx, m, 'hall')))
      : h('p', { class: 'hand-hint' }, t('Contest winners join here. '), button(t('Open the contest'), { small: true, color: 'yellow', sfx: app, onClick: () => ctx.goTab('contest') })),
    h('h3', { class: 'section-title' }, t('My Doodles')),
    h(
      'div',
      { class: 'card-grid' },
      mine.map((m) => monsterCard(ctx, m, 'mine')),
      h('button', { class: 'doodle-card add-card', type: 'button', on: { click: () => void app.scenes.go('creator', undefined) } }, h('span', { class: 'plus' }, '+'), h('span', null, t('Draw a Monster'))),
    ),
    fanMailSection(ctx),
    h('h3', { class: 'section-title' }, t('From Friends')),
    h(
      'div',
      { class: 'import-row' },
      codeInput,
      button(t('Import code'), {
        small: true,
        color: 'blue',
        badge: 'online',
        sfx: app,
        onClick: () => {
          const code = codeInput.value.trim();
          if (code.length !== 6) return app.toast(t('Share codes have 6 letters/numbers.'), 'info');
          void importByCode(ctx, code);
        },
      }),
      button(t('Import file'), { small: true, variant: 'ghost', sfx: app, onClick: () => fileInput.click() }),
      fileInput,
    ),
    shared.length ? h('div', { class: 'card-grid' }, shared.map((m) => monsterCard(ctx, m, 'shared'))) : h('p', { class: 'hand-hint' }, t('Ask a friend for a share code!')),
  );
}

/** Every monster in the book (the presets), for looking up and re-fighting. */
/** Every built-in monster on its own bookmark, with the next story fight on top. */
function wikiTab(ctx: NotebookCtx): HTMLElement {
  const next = nextStoryStage(ctx.app.store.state);
  const nextMonster = next ? PRESET_MONSTER_LIST.find((m) => m.preset === next) : undefined;
  return h(
    'div',
    { class: 'tab-wiki' },
    heading(t('Fight Monsters'), t('Pick a page and pick a fight.')),
    nextMonster
      ? h(
          'div',
          { class: 'next-fight-row' },
          button(t('Next: {monster} ▶', { monster: t(nextMonster.name) }), { variant: 'primary', color: 'pink', sfx: ctx.app, onClick: () => startFight(ctx, nextMonster.id) }),
          h('small', { class: 'hand-hint' }, t('The next page of the story.')),
        )
      : null,
    h('h3', { class: 'section-title' }, t('Back of the Book')),
    h('div', { class: 'card-grid' }, PRESET_MONSTER_LIST.map((m) => monsterCard(ctx, m, 'preset'))),
  );
}

/* ------------------------------ Equipment -------------------------------- */

type ItemDefLike = NonNullable<ReturnType<typeof itemDef>>;

/** The Gear page's open category (kept while the game is open). */
let gearSlot: ItemSlot = 'weapon';
const SLOT_INFO: Record<ItemSlot, { icon: string; title: string; hint: string }> = {
  weapon: { icon: 'sword', title: 'Weapons', hint: 'Weapons mostly add ATK: you hit harder.' },
  armor: { icon: 'shield', title: 'Armor', hint: 'Armor adds HP and DEF: you last longer.' },
  charm: { icon: 'clover', title: 'Charms', hint: 'Charms add LUCK (more critical hits) and a bit of everything.' },
};
/** Rough worth of a bonus, as the stat budget counts it (HP 1, ATK 3, DEF 2, LUCK 2). */
const bonusWorth = (b: Partial<Record<string, number>>): number => (b.hp ?? 0) + 3 * (b.atk ?? 0) + 2 * (b.def ?? 0) + 2 * (b.luck ?? 0);

function equipmentTab(ctx: NotebookCtx): HTMLElement {
  const { app } = ctx;
  const hero = app.store.hero;
  const owned = app.store.state.items;
  const slotNames: ItemSlot[] = ['weapon', 'armor', 'charm'];
  const bonusText = (b: Partial<Record<string, number>>): string =>
    Object.entries(b)
      .map(([k, v]) => `${(v ?? 0) > 0 ? '+' : ''}${v} ${k === 'hp' ? 'HP' : k.toUpperCase()}`)
      .join(', ');
  const update = (fn: () => void): void => app.store.update(['hero', 'inventory'], fn);
  const save = app.store.state;
  /** Item bonus with its upgrades added in. */
  const fullBonus = (item: ItemDefLike): Partial<Record<string, number>> => {
    const up = upgradeBonus(item, itemLevel(save, item.id));
    const out: Partial<Record<string, number>> = { ...item.bonus };
    for (const [k, v] of Object.entries(up)) out[k] = (out[k] ?? 0) + (v ?? 0);
    return out;
  };
  const plus = (id: string): string => (itemLevel(save, id) > 0 ? ` +${itemLevel(save, id)}` : '');
  const upgradeButton = (item: ItemDefLike): HTMLElement => {
    const level = itemLevel(save, item.id);
    if (level >= MAX_ITEM_LEVEL) return h('span', { class: 'stamp upgrade-max' }, t('MAX +{n}', { n: MAX_ITEM_LEVEL }));
    const cost = upgradeCost(item, level);
    return button(h('span', null, `${t('Upgrade')} +${level + 1} `, h('span', { class: 'coin', 'aria-hidden': 'true' }), ` ${cost}`), {
      small: true,
      color: 'blue',
      disabled: save.coins < cost,
      title: save.coins < cost ? t('Not enough coins yet') : t('Upgrade {name} to +{n}', { name: t(item.name), n: level + 1 }),
      sfx: app,
      onClick: () =>
        update(() => {
          if (upgradeItem(app.store.state, item.id)) app.toast(t('{item} +{n}!', { item: t(item.name), n: level + 1 }), 'success');
        }),
    });
  };
  /** One category (weapons, armor or charms): what I own, then what the shop sells. */
  const gearCategories = (): HTMLElement => {
    const slot = gearSlot;
    const info = SLOT_INFO[slot];
    const worn = hero ? itemDef(hero.equipment[slot] ?? '') : undefined;
    const wornWorth = worn ? bonusWorth(fullBonus(worn)) : 0;
    const mine = owned
      .map((id) => itemDef(id))
      .filter((i): i is ItemDefLike => i !== undefined && i.slot === slot)
      .sort((a, b) => Number(b.id === worn?.id) - Number(a.id === worn?.id) || bonusWorth(fullBonus(b)) - bonusWorth(fullBonus(a)));
    const forSale = ITEMS.filter((i) => i.slot === slot && !owned.includes(i.id)).sort((a, b) => a.price - b.price);
    const better = (item: ItemDefLike, worth: number): HTMLElement | null =>
      hero && item.id !== worn?.id && worth > wornWorth ? h('span', { class: 'better-tag' }, t('▲ Better than worn')) : null;
    const tabs = h(
      'div',
      { class: 'gear-tabs', role: 'tablist', 'aria-label': t('Equipment') },
      slotNames.map((sl) => {
        const have = owned.filter((id) => itemDef(id)?.slot === sl).length;
        const total = ITEMS.filter((i) => i.slot === sl).length;
        return h(
          'button',
          { type: 'button', role: 'tab', 'aria-selected': String(sl === slot), class: `gear-tab slot-${sl} ${sl === slot ? 'on' : ''}`, on: { click: () => ((gearSlot = sl), ctx.refresh()) } },
          icon(SLOT_INFO[sl].icon, 'gear-tab-icon'),
          h('b', null, t(SLOT_INFO[sl].title)),
          h('small', null, `${have} / ${total}`),
        );
      }),
    );
    const mineList = mine.length
      ? h(
          'ul',
          { class: 'item-list' },
          mine.map((item) => {
            const isWorn = item.id === worn?.id;
            return h(
              'li',
              { class: `item-row ${isWorn ? 'is-worn' : ''}` },
              h('b', { class: 'item-name' }, artImg(app, `item:${item.id}`, 'item-icon'), t(item.name) + plus(item.id)),
              h('span', { class: 'item-bonus' }, bonusText(fullBonus(item)), better(item, bonusWorth(fullBonus(item)))),
              h(
                'div',
                { class: 'item-actions' },
                hero
                  ? isWorn
                    ? h('span', { class: 'stamp' }, t('WORN'))
                    : button(t('Equip'), {
                        small: true,
                        color: 'green',
                        sfx: app,
                        onClick: () =>
                          update(() => {
                            const hh = app.store.hero;
                            if (hh) equip(hh, app.store.state.items, item.id);
                          }),
                      })
                  : null,
                upgradeButton(item),
              ),
            );
          }),
        )
      : h('p', { class: 'hand-hint' }, t('None yet. Win story battles or buy one below.'));
    const shopList = forSale.length
      ? h(
          'ul',
          { class: 'item-list shop' },
          forSale.map((item) =>
            h(
              'li',
              { class: 'item-row' },
              h('b', { class: 'item-name' }, artImg(app, `item:${item.id}`, 'item-icon'), t(item.name)),
              h('span', { class: 'item-bonus' }, bonusText(item.bonus), better(item, bonusWorth(item.bonus))),
              h('i', { class: 'item-note' }, t(item.note)),
              button(h('span', null, `${t('Buy')} `, h('span', { class: 'coin', 'aria-hidden': 'true' }), ` ${item.price}`), {
                small: true,
                color: 'yellow',
                disabled: save.coins < item.price,
                title: save.coins < item.price ? t('Not enough coins yet') : t('Buy {name}', { name: t(item.name) }),
                sfx: app,
                onClick: () =>
                  update(() => {
                    if (buyItem(app.store.state, item.id)) app.toast(t('Bought {name}!', { name: t(item.name) }), 'success');
                  }),
              }),
            ),
          ),
        )
      : h('p', { class: 'hand-hint' }, t('You own everything in this category!'));
    return h(
      'section',
      { class: `gear-categories slot-${slot}` },
      tabs,
      h(
        'div',
        { class: 'gear-panel' },
        h('p', { class: 'gear-hint' }, icon(info.icon, 'gear-hint-icon'), t(info.hint)),
        h('h3', { class: 'section-title' }, t('My {category}', { category: t(info.title) }), h('small', { class: 'gear-count' }, ` (${mine.length})`)),
        mine.length ? h('p', { class: 'hand-hint' }, t('Upgrade gear in your backpack with coins: each + adds a stat point.')) : null,
        mineList,
        h('h3', { class: 'section-title' }, t('School Shop · {category}', { category: t(info.title) }), h('small', { class: 'gear-count' }, ` (${forSale.length})`)),
        shopList,
      ),
    );
  };

  return h(
    'div',
    { class: 'tab-equipment' },
    heading(t('Shop'), t('Pencil case, backpack and the school shop.')),
    hero
      ? h(
          'div',
          { class: 'slot-board' },
          slotNames.map((slot) => {
            const item = itemDef(hero.equipment[slot] ?? '');
            return h(
              'div',
              {
                class: `equip-slot big slot-${slot} ${item ? 'filled' : ''} ${slot === gearSlot ? 'chosen' : ''}`,
                role: 'button',
                tabindex: '0',
                title: t(SLOT_INFO[slot].title),
                on: { click: (e: Event) => (e.target as HTMLElement).closest('button') || ((gearSlot = slot), ctx.refresh()) },
              },
              h('span', { class: 'slot-label' }, icon(SLOT_INFO[slot].icon, 'slot-icon'), t(slot)),
              item ? artImg(app, `item:${item.id}`, 'item-icon big') : null,
              h('span', { class: 'slot-item' }, item ? t(item.name) + plus(item.id) : t('— empty —')),
              item ? h('small', null, bonusText(fullBonus(item))) : null,
              item
                ? button(t('Unequip'), {
                    small: true,
                    variant: 'ghost',
                    sfx: app,
                    onClick: () =>
                      update(() => {
                        const hh = app.store.hero;
                        if (hh) unequip(hh, slot);
                      }),
                  })
                : null,
            );
          }),
        )
      : h('p', { class: 'hand-hint' }, t('Create a hero to wear equipment.')),
    gearCategories(),
    fortuneSection(ctx),
    stationeryShop(ctx),
  );
}

/** Looks-only shop: gel pens for the creator and stamps for the victory card. */
function stationeryShop(ctx: NotebookCtx): HTMLElement {
  const { app } = ctx;
  const save = app.store.state;
  const update = (fn: () => void): void => app.store.update(['inventory'], fn);
  const buy = (def: CosmeticDef): HTMLElement =>
    button(h('span', null, `${t('Buy')} `, h('span', { class: 'coin', 'aria-hidden': 'true' }), ` ${def.price}`), {
      small: true,
      color: 'yellow',
      disabled: save.coins < def.price,
      title: save.coins < def.price ? t('Not enough coins yet') : t('Buy {name}', { name: t(def.name) }),
      sfx: app,
      onClick: () =>
        update(() => {
          if (buyCosmetic(app.store.state, def.id)) app.toast(t('Bought {name}!', { name: t(def.name) }), 'success');
        }),
    });
  const card = (def: CosmeticDef): HTMLElement => {
    const owned = save.cosmetics.owned.includes(def.id);
    const using = def.kind === 'stamp' && save.cosmetics.stamp === def.id;
    return h(
      'li',
      { class: `cosmetic-card paper-scrap ${owned ? 'owned' : ''}` },
      def.kind === 'pen'
        ? h('span', { class: 'pen-swatch', style: { '--ink': def.color } as unknown as Partial<CSSStyleDeclaration>, 'aria-hidden': 'true' })
        : stampFace(app, def, 'stamp-preview'),
      h('b', { class: 'cosmetic-name' }, t(def.name)),
      h('small', { class: 'cosmetic-note' }, t(def.note)),
      !owned
        ? buy(def)
        : def.kind === 'pen'
          ? h('span', { class: 'stamp' }, t('IN YOUR PENCIL CASE'))
          : using
            ? button(t('Take off'), { small: true, variant: 'ghost', sfx: app, onClick: () => update(() => chooseStamp(app.store.state, null)) })
            : button(t('Use'), { small: true, color: 'green', sfx: app, onClick: () => update(() => chooseStamp(app.store.state, def.id)) }),
    );
  };
  return h(
    'section',
    { class: 'stationery-shop' },
    h('h3', { class: 'section-title' }, t('Stationery Shop')),
    h('p', { class: 'hand-hint' }, t('Looks only: new ink for your drawings and stamps for your wins. They never change battle stats.')),
    h('h4', { class: 'shop-shelf' }, t('Gel Pens')),
    h('ul', { class: 'cosmetic-list' }, COSMETICS.filter((c) => c.kind === 'pen' && ((!c.premium && !c.gacha) || save.cosmetics.owned.includes(c.id))).map(card)),
    h('h4', { class: 'shop-shelf' }, t('Victory Stamps')),
    h('ul', { class: 'cosmetic-list' }, COSMETICS.filter((c) => c.kind === 'stamp' && ((!c.prize && !c.premium && !c.gacha) || save.cosmetics.owned.includes(c.id))).map(card)),
    premiumShop(ctx),
  );
}

/* ------------------------------ Quests ----------------------------------- */

function questsTab(ctx: NotebookCtx): HTMLElement {
  const { app } = ctx;
  const save = app.store.state;
  const hero = app.store.hero;
  return h(
    'div',
    { class: 'tab-quests' },
    heading(t('Quest Pages'), t('The story so far — one page at a time.')),
    dailySection(ctx),
    chapterPages(ctx),
    towerSection(ctx),
    diceBoardSection(ctx),
    h('h3', { class: 'section-title' }, t('Homework Quests')),
    h('p', { class: 'hand-hint' }, t('Homework, but fun.')),
    h(
      'ol',
      { class: 'quest-list' },
      QUESTS.map((q) => {
        const p = questProgress(save, hero, q);
        const claimed = save.quests.claimed.includes(q.id);
        const ready = !claimed && p >= q.goal;
        return h(
          'li',
          { class: `quest ${claimed ? 'claimed' : ''} ${ready ? 'ready' : ''}` },
          h('span', { class: 'checkbox', 'aria-hidden': 'true' }, claimed ? doodleSvg('check') : null),
          h('div', { class: 'quest-body' }, h('b', null, t(q.title)), h('p', null, t(q.note)), meter(p, q.goal, 'exp', `${p} / ${q.goal}`)),
          h(
            'div',
            { class: 'quest-reward' },
            h('small', null, `${t('{exp} EXP · {coins} coins', { exp: q.reward.exp, coins: q.reward.coins })}${q.reward.item ? ` · ${t(itemDef(q.reward.item)?.name ?? '')}` : ''}`),
            ready
              ? button(t('Claim!'), {
                  small: true,
                  variant: 'primary',
                  color: 'yellow',
                  sfx: app,
                  onClick: () =>
                    app.store.update(['quests', 'hero', 'inventory', 'memories'], (s) => {
                      const done = claimQuest(s, app.store.hero, q.id);
                      if (done) {
                        app.sfx.play('win');
                        app.toast(t('Quest complete: {name}!', { name: t(done.title) }), 'success');
                      }
                    }),
                })
              : null,
          ),
        );
      }),
    ),
  );
}

/* ------------------------------ Memories --------------------------------- */

function memoriesTab(ctx: NotebookCtx): HTMLElement {
  const mems = ctx.app.store.state.memories;
  return h(
    'div',
    { class: 'tab-memories' },
    collectionStrip(ctx, 'memories'),
    heading(t('Diary'), t('Dear diary…')),
    mems.length
      ? h(
          'ul',
          { class: 'memory-list' },
          mems.map((m) =>
            h(
              'li',
              { class: `memory kind-${m.kind}` },
              artImg(ctx.app, `sticker:${m.sticker ?? 'star'}`, 'sticker', doodleSvg(m.sticker ?? 'star', 'sticker')),
              h('div', null, h('span', { class: 'memory-date' }, formatDate(m.at)), h('b', null, m.title), h('p', null, m.text)),
            ),
          ),
        )
      : h('p', { class: 'hand-hint' }, t('Nothing here yet. Go make some memories!')),
  );
}

/* ------------------------------ Friends ---------------------------------- */

/** XXXXXXXX → XXXX-XXXX, easier to read out loud in class. */
const formatFriendCode = (code: string): string => (code.length === 8 ? `${code.slice(0, 4)}-${code.slice(4)}` : code);

const capitalize = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/** "3W / 1L · last seen 12 May" for a recent rival. */
const rivalMeta = (r: { wins: number; losses: number; lastSeen: Parameters<typeof formatDate>[0] }): string =>
  t('{wins}W / {losses}L · last seen {date}', { wins: r.wins, losses: r.losses, date: formatDate(r.lastSeen) });

/**
 * Server half of the Friends page: friend code, add-by-code, requests,
 * friends (with who is online and battle invites) and recent rivals.
 * Re-fetched after every action and on live friend pushes; presence changes
 * only redraw the friends list.
 */
function friendsOnline(ctx: NotebookCtx): HTMLElement {
  const { app } = ctx;
  const box = h('div', { class: 'friends-online' }, h('p', { class: 'hand-hint' }, t('Opening the class address book…')));
  const friendList = h('div', { class: 'my-friends' });
  const daresBox = h('div', { class: 'dares-box' });
  let friends: FriendDTO[] = [];
  // Kept across re-renders so a live update never wipes a half-typed code.
  const codeInput = h('input', { class: 'hand-input code friend-code-input', maxLength: 9, placeholder: 'ABCD-EFGH', 'aria-label': t('Friend code'), autocomplete: 'off' });
  const send = (): void => {
    const code = codeInput.value.replace(/[\s-]/g, '').toUpperCase();
    if (code.length !== 8) return app.toast(t('Friend codes have 8 letters/numbers, like ABCD-EFGH.'), 'info');
    void act(async () => {
      const f = await app.api.addFriend({ friendCode: code });
      codeInput.value = '';
      return f.status === 'accepted' ? t('You and {name} are now friends!', { name: f.name }) : t('Friend request sent to {name}.', { name: f.name });
    });
  };
  codeInput.addEventListener('keydown', (e) => e.key === 'Enter' && send());

  for (const t of ['friend.request', 'friend.accepted', 'friend.removed', 'dare.received', 'dare.answered', 'dare.removed'] as const) ctx.onCleanup(app.mp.on(t, () => void load()));
  ctx.onCleanup(
    app.mp.on('friend.online', (m) => {
      const f = friends.find((x) => x.id === m.id);
      if (!f) return;
      f.online = m.online;
      renderFriends();
    }),
  );

  const act = async (fn: () => Promise<string | void>): Promise<void> => {
    try {
      const msg = await fn();
      if (msg) app.toast(msg, 'success');
    } catch (err) {
      app.toast(err instanceof Error ? capitalize(err.message) : t('Something went wrong.'), 'error');
    }
    await load();
  };

  const row = (f: FriendDTO, ...actions: (HTMLElement | null)[]): HTMLElement => {
    const rival = app.store.state.friends.find((r) => r.id === f.id);
    const friend = f.status === 'accepted';
    return h(
      'li',
      { class: `friend ${friend && f.online ? 'is-online' : ''}`, 'data-friend': f.id },
      artImg(app, 'sticker:heart', 'sticker', doodleSvg('heart', 'sticker')),
      friend ? h('span', { class: 'presence-dot', role: 'img', 'aria-label': f.online ? t('online now') : t('offline'), title: f.online ? t('online now') : t('offline') }) : null,
      h('b', null, f.name),
      h(
        'span',
        { class: 'friend-meta' },
        ` ${f.trophies} 🏆${rival ? ` · ${t('{wins}W / {losses}L vs you', { wins: rival.wins, losses: rival.losses })}` : ''}${friend && f.online ? ` · ${t('online now')}` : ''}`,
      ),
      h('span', { class: 'friend-actions' }, actions),
    );
  };

  /** "My Friends": online friends first, each with Invite and Unfriend. */
  const renderFriends = (): void => {
    const sorted = [...friends].sort((a, b) => Number(b.online) - Number(a.online));
    setChildren(
      friendList,
      h(
        'h3',
        { class: 'section-title' },
        friends.length
          ? t('My Friends ({n} · {online} online)', { n: friends.length, online: friends.filter((f) => f.online).length })
          : t('My Friends ({n})', { n: friends.length }),
      ),
      friends.length
        ? h(
            'ul',
            { class: 'friend-list' },
            sorted.map((f) =>
              row(
                f,
                button(t('Notebook'), {
                  small: true,
                  color: 'blue',
                  title: t("Open {name}'s notebook", { name: f.name }),
                  sfx: app,
                  onClick: () => void openFriendNotebook(ctx, f),
                }),
                button(t('Dare'), {
                  small: true,
                  color: 'yellow',
                  title: t('Send {name} one of your monsters to beat', { name: f.name }),
                  sfx: app,
                  onClick: () => void sendDareDialog(ctx, { id: f.id, name: f.name }, () => void load()),
                }),
                button(t('Invite to battle'), {
                  small: true,
                  color: 'pink',
                  disabled: !f.online,
                  title: f.online ? t('Invite {name} to a battle', { name: f.name }) : t('{name} is offline — invites need them online', { name: f.name }),
                  sfx: app,
                  onClick: () => void app.scenes.go('multiplayer', { inviteFriend: { id: f.id, name: f.name } }),
                }),
                button(t('Unfriend'), {
                  small: true,
                  variant: 'ghost',
                  sfx: app,
                  onClick: () =>
                    void confirmBox(t('Unfriend?'), t('Remove {name} from your friends?', { name: f.name }), t('Unfriend')).then((yes) => {
                      if (yes) void act(async () => (await app.api.removeFriend(f.id), t('{name} was erased from your friends.', { name: f.name })));
                    }),
                }),
              ),
            ),
          )
        : h('p', { class: 'hand-hint' }, t('No friends yet. Swap friend codes with a classmate!')),
    );
  };

  const render = (me: PlayerDTO, res: FriendsResponse, dares: DaresResponse | null): void => {
    ctx.setFriendRequests(res.incoming.length + (dares ? unansweredDares(dares) : 0));
    if (dares) setChildren(daresBox, daresSection(ctx, dares, () => void load()));
    friends = res.friends;
    renderFriends();
    const linked = new Set([...res.friends, ...res.incoming, ...res.outgoing].map((f) => f.id));
    const rivals = app.store.state.friends.filter((r) => !linked.has(r.id) && r.id !== me.id);

    setChildren(
      box,
      stickyNote(
        'yellow',
        h('p', null, t('Your friend code — tell your classmates:')),
        h('p', { class: 'share-code friend-code' }, formatFriendCode(me.friendCode)),
        button(t('Copy code'), {
          small: true,
          variant: 'ghost',
          sfx: app,
          onClick: () =>
            void navigator.clipboard?.writeText(formatFriendCode(me.friendCode)).then(
              () => app.toast(t('Friend code copied!'), 'success'),
              () => app.toast(t('Could not copy. Write it down instead!'), 'info'),
            ),
        }),
      ),
      h('h3', { class: 'section-title' }, t('Add a Friend')),
      h('div', { class: 'import-row' }, codeInput, button(t('Send request'), { small: true, color: 'blue', sfx: app, onClick: send })),
      res.incoming.length
        ? h(
            'div',
            { class: 'friend-requests' },
            h('h3', { class: 'section-title' }, t('Friend Requests ({n})', { n: res.incoming.length })),
            h(
              'ul',
              { class: 'friend-list' },
              res.incoming.map((f) =>
                row(
                  f,
                  button(t('Accept'), {
                    small: true,
                    color: 'green',
                    sfx: app,
                    onClick: () => void act(async () => t('You and {name} are now friends!', { name: (await app.api.acceptFriend(f.id)).name })),
                  }),
                  button(t('Decline'), { small: true, variant: 'ghost', sfx: app, onClick: () => void act(() => app.api.removeFriend(f.id)) }),
                ),
              ),
            ),
          )
        : null,
      friendList,
      daresBox,
      res.outgoing.length
        ? h(
            'div',
            null,
            h('h3', { class: 'section-title' }, t('Waiting for an Answer')),
            h('ul', { class: 'friend-list' }, res.outgoing.map((f) => row(f, button(t('Cancel'), { small: true, variant: 'ghost', sfx: app, onClick: () => void act(() => app.api.removeFriend(f.id)) })))),
          )
        : null,
      rivals.length
        ? h(
            'div',
            null,
            h('h3', { class: 'section-title' }, t('Recent Rivals')),
            h(
              'ul',
              { class: 'friend-list' },
              rivals.map((r) =>
                h(
                  'li',
                  { class: 'friend' },
                  artImg(app, 'sticker:skull', 'sticker', doodleSvg('skull', 'sticker')),
                  h('b', null, r.name),
                  h('span', { class: 'friend-meta' }, ` ${rivalMeta(r)}`),
                  h(
                    'span',
                    { class: 'friend-actions' },
                    button(t('Add friend'), {
                      small: true,
                      color: 'blue',
                      sfx: app,
                      onClick: () =>
                        void act(async () => {
                          const f = await app.api.addFriend({ playerId: r.id });
                          return f.status === 'accepted' ? t('You and {name} are now friends!', { name: f.name }) : t('Friend request sent to {name}.', { name: f.name });
                        }),
                    }),
                  ),
                ),
              ),
            ),
          )
        : null,
    );
  };

  const load = async (): Promise<void> => {
    if (!(await ensureOnline(app, false))) {
      box.replaceChildren(h('p', { class: 'hand-hint' }, t('Battle server offline — friends are unavailable right now.')));
      return;
    }
    try {
      const [me, res, dares] = await Promise.all([app.api.me(), app.api.friends(), refreshDares(app)]);
      render(me, res, dares);
      // Friends who open my notebook see the hero and stamps I have now.
      void publishNotebookCard(app);
    } catch (err) {
      box.replaceChildren(h('p', { class: 'hand-hint' }, err instanceof Error ? err.message : t('Could not load your friends.')));
    }
  };
  void load();
  return box;
}

function friendsTab(ctx: NotebookCtx): HTMLElement {
  const { app } = ctx;
  const save = app.store.state;
  // The server rankings (top 5 of each board here; "See all" opens the full list).
  const board = h('div', { class: 'leaderboard' }, h('h3', { class: 'section-title' }, '🏆 ', t('Rankings')), rankingsPanel(app, 'trophies', { limit: 5, onMore: () => void openRankings(app) }));
  return h(
    'div',
    { class: 'tab-friends' },
    heading(t('PVP'), t('Battle your friends and climb the class ranking.')),
    stickyNote(
      'blue',
      save.online ? h('p', null, `${t('Signed in as')} `, h('b', null, save.online.name)) : h('p', null, t('Sign in to the battle server to add friends.')),
      save.online && isKid(save)
        ? h(
            'p',
            { class: 'hand-hint kid-name' },
            t('In kid mode the game picks your online name, so it is always friendly.'),
            ' ',
            button(t('New name'), {
              small: true,
              color: 'green',
              sfx: app,
              onClick: async () => {
                try {
                  const p = await app.api.patchMe({ reroll: true, lang: language() });
                  app.store.update(['online'], (s) => s.online && (s.online.name = p.name));
                } catch (err) {
                  app.toast(err instanceof Error ? err.message : t('Server unavailable'), 'error');
                }
              },
            }),
          )
        : null,
      h(
        'div',
        { class: 'row-actions' },
        save.online
          ? null
          : button(t('Sign in'), { variant: 'primary', color: 'yellow', sfx: app, onClick: () => void ensureOnline(app) }),
        button(t('Battle With Friends'), { variant: 'primary', color: 'pink', sfx: app, onClick: () => void app.scenes.go('multiplayer', undefined) }),
      ),
    ),
    save.online ? classPeek(ctx) : null,
    save.online
      ? friendsOnline(ctx)
      : save.friends.length
        ? h(
            'div',
            null,
            h('h3', { class: 'section-title' }, t('Recent Rivals')),
            h(
              'ul',
              { class: 'friend-list' },
              save.friends.map((f) => h('li', { class: 'friend' }, artImg(app, 'sticker:skull', 'sticker', doodleSvg('skull', 'sticker')), h('b', null, f.name), h('span', { class: 'friend-meta' }, ` ${rivalMeta(f)}`))),
            ),
          )
        : h('p', { class: 'hand-hint' }, t('No rivals yet. Share a room code with a friend!')),
    board,
  );
}

/* ------------------------------ Settings --------------------------------- */

function settingsTab(ctx: NotebookCtx): HTMLElement {
  const { app } = ctx;
  const s = app.store.state.settings;
  const set = (fn: (st: typeof s) => void): void => app.store.update(['settings'], (st) => fn(st.settings));
  const select = <T extends string | number>(label: string, value: T, options: [T, string][], onChange: (v: T) => void): HTMLElement =>
    h(
      'label',
      { class: 'field' },
      h('span', null, label),
      h(
        'select',
        { class: 'hand-select', on: { change: (e: Event) => onChange(options.find(([v]) => String(v) === (e.target as HTMLSelectElement).value)![0]) } },
        options.map(([v, text]) => h('option', { value: String(v), selected: v === value }, text)),
      ),
    );
  const fileInput = h('input', { type: 'file', accept: '.json,application/json', class: 'visually-hidden', 'aria-label': t('Import notebook file') });
  fileInput.addEventListener('change', async () => {
    const f = fileInput.files?.[0];
    if (!f) return;
    try {
      const next = sanitizeSave(JSON.parse(await f.text()));
      // The age band belongs to whoever plays here, not to the file (no way around kid mode).
      next.ageGroup = app.store.state.ageGroup;
      if (!(await confirmBox(t('Load notebook?'), t('This replaces your current notebook with the file.'), t('Load')))) return;
      app.store.replace(next);
      app.toast(t('Notebook loaded!'), 'success');
    } catch {
      app.toast(t('That file is not a Paper Legends notebook.'), 'error');
    }
  });
  return h(
    'div',
    { class: 'tab-settings' },
    heading(t('Options'), t('Scribbled on the inside cover.')),
    h('div', { class: 'row-actions' }, button(t('How to Play'), { color: 'blue', sfx: app, onClick: () => void app.scenes.go('help', undefined, 'page') })),
    select('Language / ภาษา', s.language, LANGUAGES.map((l): [LanguageSetting, string] => [l.id, l.id === 'auto' ? t('Auto (follow my device)') : l.label]), (v) => {
      set((st) => (st.language = v));
      app.store.flush();
      // Every screen is rebuilt in the new language.
      location.reload();
    }),
    h(
      'label',
      { class: 'field' },
      h('span', null, t('Volume')),
      h('input', { type: 'range', min: '0', max: '1', step: '0.05', value: String(s.volume), class: 'hand-range', on: { change: (e: Event) => set((st) => (st.volume = Number((e.target as HTMLInputElement).value))) } }),
    ),
    h(
      'label',
      { class: 'field' },
      h('span', null, t('Music volume')),
      h('input', { type: 'range', min: '0', max: '1', step: '0.05', value: String(s.music), class: 'hand-range', on: { change: (e: Event) => set((st) => (st.music = Number((e.target as HTMLInputElement).value))) } }),
    ),
    h('label', { class: 'check-field' }, h('input', { type: 'checkbox', checked: s.muted, on: { change: (e: Event) => set((st) => (st.muted = (e.target as HTMLInputElement).checked)) } }), h('span', null, t('Mute sounds'))),
    select(t('Reduce motion'), s.reducedMotion, [['auto', t('Follow my device')], ['on', t('On — calmer animations')], ['off', t('Off — full animation')]], (v) => set((st) => (st.reducedMotion = v))),
    select(t('Frame rate'), s.fpsCap, [[60, '60 FPS'], [30, t('30 FPS (battery saver)')]], (v) => set((st) => (st.fpsCap = v))),
    h('label', { class: 'check-field' }, h('input', { type: 'checkbox', checked: s.traceGame, on: { change: (e: Event) => set((st) => (st.traceGame = (e.target as HTMLInputElement).checked)) } }), h('span', null, t('Ink Strike: trace a shape after some won rounds for an extra hit'))),
    h('label', { class: 'check-field' }, h('input', { type: 'checkbox', checked: s.fullscreenOnPhone, on: { change: (e: Event) => set((st) => (st.fullscreenOnPhone = (e.target as HTMLInputElement).checked)) } }), h('span', null, t('Full screen on phones (the first tap opens it)'))),
    select(t('Graphics'), s.renderMode, [['cpu', t('CPU — easy on the graphics card (recommended)')], ['gpu', t('GPU — smoother on strong computers')]], (v) => {
      set((st) => (st.renderMode = v));
      app.toast(t('Reload the game to switch.'), 'info');
    }),
    select(t('Sharpness'), s.dprCap, [[2, t('High (retina)')], [1.5, t('Medium')], [1, t('Low (faster)')]], (v) => set((st) => (st.dprCap = v))),
    h('label', { class: 'check-field' }, h('input', { type: 'checkbox', checked: s.debug, on: { change: (e: Event) => set((st) => (st.debug = (e.target as HTMLInputElement).checked)) } }), h('span', null, t('Show debug overlay (FPS, frame time, objects) — F3'))),
    h('h3', { class: 'section-title' }, t('Family')),
    h(
      'p',
      { class: 'hand-hint' },
      app.store.state.ageGroup === 'adult'
        ? t('Age: 18 or older.')
        : app.store.state.ageGroup === 'teen'
          ? t('Age: 13 to 17. A grown-up approves any real-money purchase.')
          : t('Kid mode (under 13): the game picks your online name, and a grown-up approves any real-money purchase.'),
    ),
    h('div', { class: 'row-actions' }, button(t('Change age'), { small: true, sfx: app, onClick: () => void changeAge(app).then(() => ctx.refresh()) })),
    h('h3', { class: 'section-title' }, t('Account')),
    accountSection(app, () => ctx.refresh()),
    h('h3', { class: 'section-title' }, t('Suggest an idea')),
    h('p', { class: 'hand-hint' }, t('Tell the team what you would like in the game, or a bug you found.')),
    h('div', { class: 'row-actions' }, button(t('💡 Suggest an idea'), { small: true, color: 'green', sfx: app, onClick: () => void openFeedback(app) })),
    h('h3', { class: 'section-title' }, t('Save & Load')),
    h(
      'p',
      { class: 'hand-hint' },
      app.store.state.savedAt
        ? t('Saved automatically in this browser · last save {time}.', { time: new Date(app.store.state.savedAt).toLocaleTimeString() })
        : t('Saved automatically in this browser.'),
    ),
    h(
      'div',
      { class: 'row-actions' },
      button(t('Export notebook'), {
        small: true,
        color: 'blue',
        sfx: app,
        onClick: () => {
          app.store.flush();
          download('paper-legends-notebook.json', JSON.stringify(app.store.state, null, 1));
        },
      }),
      button(t('Import notebook'), { small: true, sfx: app, onClick: () => fileInput.click() }),
      fileInput,
      button(t('Erase everything'), {
        small: true,
        variant: 'danger',
        sfx: app,
        onClick: async () => {
          if (!(await confirmBox(t('Erase the whole notebook?'), t('Heroes, monsters, items and memories will be rubbed out. This cannot be undone.'), t('Erase it all')))) return;
          const keepSettings = app.store.state.settings;
          const fresh = newSave();
          fresh.settings = keepSettings;
          fresh.ageGroup = app.store.state.ageGroup;
          app.store.replace(fresh);
          app.sfx.play('erase');
          ctx.goTab('newhero');
        },
      }),
    ),
  );
}

export function download(filename: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
  const a = h('a', { href: url, download: filename });
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function showShareCode(app: App, name: string, code: string): Promise<void> {
  await modal(
    t('Share with a friend'),
    h('div', { class: 'share-box' }, h('p', null, t('Tell your friend to type this code in Collection → From Friends to get {name}:', { name })), h('p', { class: 'share-code' }, code)),
    [
      { label: t('Copy code'), value: 'copy', color: 'blue' },
      { label: t('Done'), value: 'ok', variant: 'primary', color: 'yellow' },
    ],
  ).then((v) => {
    if (v === 'copy') void navigator.clipboard?.writeText(code).then(() => app.toast(t('Copied!'), 'success'));
  });
}

export const TAB_RENDERERS: Record<TabId, (ctx: NotebookCtx) => HTMLElement> = {
  hero: heroTab,
  newhero: newHeroTab,
  monsters: monstersTab,
  wiki: wikiTab,
  equipment: equipmentTab,
  quests: questsTab,
  memories: memoriesTab,
  friends: friendsTab,
  class: classTab,
  contest: contestTab,
  settings: settingsTab,
};

